import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { Pagination } from '../components/common/Pagination';
import { LiveIndicator } from '../components/common/LiveIndicator';
import { getBlocks, getNetworkStats } from '../lib/api';
import { formatNumber, timeAgo, formatBytes } from '../lib/utils';

export default function BlocksPage() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalBlocks, setTotalBlocks] = useState(0);
  const navigate = useNavigate();
  const limit = 20;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [blocksRes, statsRes] = await Promise.all([
          getBlocks(currentPage, limit),
          getNetworkStats(),
        ]);
        setBlocks(blocksRes.data);
        setTotalBlocks(statsRes.data.current_height);
      } catch (error) {
        console.error('Error fetching blocks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage]);

  const totalPages = Math.ceil(totalBlocks / limit);

  const columns = [
    {
      header: 'Height',
      cell: (row) => (
        <Link to={`/block/${row.height}`} className="text-primary hover:underline font-mono font-bold">
          #{formatNumber(row.height, 0)}
        </Link>
      ),
    },
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} link={`/block/${row.height}`} />,
    },
    {
      header: 'Miner',
      cell: (row) => <HashDisplay hash={row.miner} type="address" link={`/address/${row.miner}`} />,
    },
    {
      header: 'Txns',
      cell: (row) => <span className="font-mono">{row.tx_count}</span>,
    },
    {
      header: 'Size',
      cell: (row) => <span className="font-mono text-sm text-muted-foreground">{formatBytes(row.size)}</span>,
    },
    {
      header: 'Reward',
      cell: (row) => (
        <div className="text-right">
          <span className="text-green-400 font-mono text-sm">
            {row.reward.total.toFixed(6)} GG
          </span>
          <div className="text-xs text-muted-foreground">
            PoW: {row.reward.pow.toFixed(4)} | PoS: {row.reward.pos.toFixed(4)}
          </div>
        </div>
      ),
    },
    {
      header: 'Time',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.timestamp)}</span>,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="blocks-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-transparent">
              <Box className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Blocks</h1>
              <p className="text-sm text-muted-foreground">
                {formatNumber(totalBlocks, 0)} blocks found
              </p>
            </div>
          </div>
          <LiveIndicator />
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={blocks}
          loading={loading}
          emptyMessage="No blocks found"
          onRowClick={(row) => navigate(`/block/${row.height}`)}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </Layout>
  );
}
